import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Rocket, 
  Zap, 
  Flame, 
  Star,
  Sparkles,
  CheckCircle2,
  Clock,
  TrendingUp,
  ArrowLeft,
  Crown,
  RefreshCw,
  AlertTriangle,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { UserModelData, PAGE_NAME } from "../../../types";
import useThemeHook from "../../hooks/useThemeHook";
import CommonPageTitle from "../../components/CommonPageTitle";
import arthereumProvider from "../../../backend/ArthereumProvider";
import { actions } from "../../redux";
import piNetworkAdsManager from "../../utils/PiNetworkAdsManager";

interface ForgeItem {
  id: string;
  name: string;
  description: string;
  price: number;
  icon: React.ReactNode;
  bgGradient: string;
  effect: string;
  duration: string;
  multiplier: string;
  popular?: boolean;
  limited?: boolean;
  savings?: string;
}

const ForgePage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useThemeHook();
  const userData: UserModelData = useSelector((state: any) => state?.actualUser);
  const user = userData?.user;

  const [loading, setLoading] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [purchasedItem, setPurchasedItem] = useState<string>("");

  const piBalance = user?.picoinBalance || 0;

  const forgeItems: ForgeItem[] = [
    {
      id: "streak_resurrection",
      name: "Streak Resurrection",
      description: "Lost your streak? Bring it back from the dead! Don't lose all that progress.",
      price: 50,
      icon: <Flame size={28} className="text-orange-400" />,
      bgGradient: "from-orange-600/30 to-red-600/30",
      effect: "Restore your lost streak",
      duration: "Instant",
      multiplier: "Save 100+ days of effort",
      limited: true,
    },
    {
      id: "flash_boost",
      name: "Flash Boost",
      description: "Triple your next mining grant! Limited time - strike while hot!",
      price: 25,
      icon: <Zap size={28} className="text-yellow-400" />,
      bgGradient: "from-yellow-600/30 to-amber-600/30",
      effect: "3x next grant",
      duration: "Next claim",
      multiplier: "+200% earnings",
      popular: true,
    },
    {
      id: "turbo_charge",
      name: "Turbo Charge",
      description: "Supercharge ALL your grants for 24 hours. Maximum earnings mode!",
      price: 100,
      icon: <Rocket size={28} className="text-purple-400" />,
      bgGradient: "from-purple-600/30 to-pink-600/30",
      effect: "2x ALL grants",
      duration: "24 hours",
      multiplier: "+100% for 24h",
      savings: "Best value!",
    },
    {
      id: "lucky_charm",
      name: "Lucky Charm",
      description: "Increase your Lucky Spin rewards by 5x! Luck favors the bold.",
      price: 35,
      icon: <Star size={28} className="text-cyan-400" />,
      bgGradient: "from-cyan-600/30 to-blue-600/30",
      effect: "5x spin rewards",
      duration: "Next 3 spins",
      multiplier: "+400% spin value",
    },
    {
      id: "instant_refill",
      name: "Instant Refill",
      description: "Reset your mining cooldown to zero. Claim your next grant NOW!",
      price: 30,
      icon: <Zap size={28} className="text-emerald-400" />,
      bgGradient: "from-emerald-600/30 to-teal-600/30",
      effect: "Reset mining timer",
      duration: "Instant",
      multiplier: "Claim immediately",
      popular: true,
    },
    {
      id: "immunity_shield",
      name: "Immunity Shield",
      description: "Protect your login streak for 3 days. Never lose progress again!",
      price: 40,
      icon: <Star size={28} className="text-blue-400" />,
      bgGradient: "from-blue-600/30 to-indigo-600/30",
      effect: "Streak protection",
      duration: "3 days",
      multiplier: "Save your streak",
      limited: true,
    },
    {
      id: "double_referral",
      name: "Double Referral",
      description: "Earn 2x referral rewards for 7 days. 20% commission instead of 10%!",
      price: 75,
      icon: <TrendingUp size={28} className="text-pink-400" />,
      bgGradient: "from-pink-600/30 to-rose-600/30",
      effect: "2x referral earnings",
      duration: "7 days",
      multiplier: "+100% referral income",
      savings: "EXCLUSIVE",
    },
  ];

  const handlePurchase = async (item: ForgeItem) => {
    if (loading) return;
    
    if (piBalance < item.price) {
      alert(`Not enough Pi! You need ${item.price} π but only have ${piBalance.toFixed(2)} π`);
      return;
    }

    setLoading(item.id);
    try {
      // Call backend to process purchase
      await arthereumProvider.purchaseForgeItem(user!.id, item.id, item.price);
      
      // Update Redux locally instead of re-fetching from Firestore
      dispatch(actions.setActualUser({
        ...userData,
        user: {
          ...user,
          picoinBalance: (user!.picoinBalance || 0) - item.price,
        },
        miningInfos: userData.miningInfos,
      }));
      
      setPurchasedItem(item.name);
      setShowSuccess(true);
    } catch (error: any) {
      alert(error.message || "Purchase failed. Please try again.");
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className={`min-h-screen ${theme === "dark" ? "bg-slate-900" : "bg-gray-50"}`}>
      <CommonPageTitle title="Arthereum Forge" padding={null} />
      
      <div className="max-w-lg mx-auto px-6 py-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button 
            onClick={() => navigate(-1)}
            className={`p-2 rounded-xl ${theme === "dark" ? "bg-slate-800" : "bg-white"}`}
          >
            <ArrowLeft size={20} className={theme === "dark" ? "text-white" : "text-gray-900"} />
          </button>
          <div>
            <h1 className={`text-2xl font-black ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
              Arthereum Forge 🔥
            </h1>
            <p className={`text-sm ${theme === "dark" ? "text-slate-400" : "text-gray-500"}`}>
              Forge powerful boosts with Pi
            </p>
          </div>
        </div>

        {/* Balance Card */}
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className={`
            p-4 rounded-2xl mb-6 flex items-center justify-between
            ${theme === "dark" ? "bg-gradient-to-r from-amber-900/50 to-orange-900/50 border border-amber-500/20" : "bg-gradient-to-r from-amber-100 to-orange-100 border border-amber-300"}
          `}
        >
          <div>
            <p className={`text-xs font-bold uppercase tracking-wider ${theme === "dark" ? "text-amber-400" : "text-amber-600"}`}>
              Your Pi Balance
            </p>
            <p className={`text-2xl font-black ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
              {piBalance.toFixed(2)} π
            </p>
          </div>
          <button 
            onClick={() => navigate(PAGE_NAME.DEPOSIT)}
            className="px-4 py-2 rounded-xl bg-amber-500 text-white font-bold text-sm hover:bg-amber-600 transition-colors"
          >
            + Add Pi
          </button>
        </motion.div>

        {/* Urgency Banner */}
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className={`
            p-3 rounded-xl mb-6 flex items-center gap-3
            bg-gradient-to-r from-red-600/20 to-orange-600/20 border border-red-500/30
          `}
        >
          <AlertTriangle size={20} className="text-red-400 shrink-0" />
          <p className={`text-xs ${theme === "dark" ? "text-red-300" : "text-red-600"}`}>
            <strong>⏰ Limited time!</strong> Forge prices may increase. Lock in today's rates now!
          </p>
        </motion.div>

        {/* Flash Sale */}
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: [0.95, 1.02, 1], opacity: 1 }}
          className={`p-4 rounded-2xl mb-6 border-2 relative overflow-hidden ${
            theme === "dark" ? "bg-gradient-to-r from-amber-900/30 to-orange-900/30 border-amber-500/40" : "bg-gradient-to-r from-amber-50 to-orange-50 border-amber-400"
          }`}
        >
          <div className="absolute top-0 right-0 px-3 py-1 bg-red-500 text-white text-[10px] font-black rounded-bl-xl animate-pulse">
            ⏰ FLASH SALE
          </div>
          <div className="flex items-center gap-3 pt-2">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${theme === "dark" ? "bg-amber-500/20" : "bg-amber-100"}`}>
              <Flame size={24} className="text-amber-500" />
            </div>
            <div className="flex-1">
              <p className={`font-black text-sm ${theme === "dark" ? "text-amber-300" : "text-amber-800"}`}>
                Mega Flash Boost — 20x Mining!
              </p>
              <p className={`text-[10px] ${theme === "dark" ? "text-amber-400/70" : "text-amber-600"}`}>
                48 hours of 20x mining power · Limited supply!
              </p>
            </div>
            <button
              onClick={() => handlePurchase({ id: 'mega_flash_boost', name: 'Mega Flash Boost', price: 200, icon: null, bgGradient: '', effect: '20x mining', duration: '48h', multiplier: '+1900%' } as any)}
              disabled={piBalance < 200}
              className={`px-4 py-2 rounded-xl font-bold text-xs transition-all active:scale-95 ${
                piBalance < 200 ? 'bg-slate-600 text-slate-400' : 'bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:shadow-lg'
              }`}
            >
              {piBalance < 200 ? 'Need 200π' : '200π 🔥'}
            </button>
          </div>
        </motion.div>

        {/* Forge Items */}
        <div className="space-y-4">
          {forgeItems.map((item, idx) => (
            <motion.div
              key={item.id}
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: idx * 0.1 }}
              className={`
                relative overflow-hidden rounded-2xl p-5
                bg-gradient-to-r ${item.bgGradient}
                border ${theme === "dark" ? "border-white/10" : "border-gray-200"}
                ${theme === "dark" ? "bg-slate-800/80" : "bg-white/90"}
              `}
            >
              {/* Badges */}
              <div className="absolute top-3 right-3 flex gap-2">
                {item.popular && (
                  <span className="px-2 py-1 rounded-full text-[10px] font-bold bg-gradient-to-r from-amber-500 to-orange-500 text-white">
                    🔥 MOST POPULAR
                  </span>
                )}
                {item.limited && (
                  <span className="px-2 py-1 rounded-full text-[10px] font-bold bg-red-500 text-white animate-pulse">
                    ⏰ LIMITED
                  </span>
                )}
                {item.savings && (
                  <span className="px-2 py-1 rounded-full text-[10px] font-bold bg-emerald-500 text-white">
                    {item.savings}
                  </span>
                )}
              </div>

              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-xl shrink-0 ${theme === "dark" ? "bg-slate-700/50" : "bg-white/50"}`}>
                  {item.icon}
                </div>
                
                <div className="flex-1 min-w-0">
                  <h3 className={`font-bold text-lg ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
                    {item.name}
                  </h3>
                  <p className={`text-sm ${theme === "dark" ? "text-slate-400" : "text-gray-500"} mb-3`}>
                    {item.description}
                  </p>
                  
                  {/* Stats */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    <span className={`text-xs px-2 py-1 rounded-lg flex items-center gap-1 ${theme === "dark" ? "bg-emerald-900/50 text-emerald-400" : "bg-emerald-100 text-emerald-600"}`}>
                      <TrendingUp size={12} /> {item.multiplier}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded-lg flex items-center gap-1 ${theme === "dark" ? "bg-blue-900/50 text-blue-400" : "bg-blue-100 text-blue-600"}`}>
                      <Clock size={12} /> {item.duration}
                    </span>
                  </div>

                  {/* Purchase Button */}
                  <button
                    onClick={() => handlePurchase(item)}
                    disabled={loading === item.id || piBalance < item.price}
                    className={`
                      w-full py-3 rounded-xl font-bold text-sm transition-all transform active:scale-95 flex items-center justify-center gap-2
                      ${piBalance < item.price 
                        ? 'bg-slate-600 text-slate-400 cursor-not-allowed' 
                        : 'bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:shadow-lg hover:shadow-amber-500/30'
                      }
                    `}
                  >
                    {loading === item.id ? (
                      <RefreshCw className="animate-spin" size={18} />
                    ) : piBalance < item.price ? (
                      <>Need {item.price} π</>
                    ) : (
                      <>
                        <Sparkles size={16} />
                        Forge for {item.price} π
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Social Proof */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5 }}
          className={`
            mt-6 p-4 rounded-2xl text-center
            ${theme === "dark" ? "bg-slate-800/50 border border-slate-700" : "bg-white border border-gray-200"}
          `}
        >
          <div className="flex items-center justify-center gap-2 mb-2">
            <div className="flex -space-x-2">
              {['🧑', '👨', '👩', '🧔'].map((emoji, i) => (
                <span key={i} className="w-6 h-6 rounded-full bg-slate-700 flex items-center justify-center text-xs border-2 border-slate-800">
                  {emoji}
                </span>
              ))}
            </div>
            <span className={`text-sm font-bold ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
              +2,847 forged today
            </span>
          </div>
          <p className={`text-xs ${theme === "dark" ? "text-slate-400" : "text-gray-500"}`}>
            Join thousands of users maximizing their earnings
          </p>
        </motion.div>
      </div>

      {/* Success Modal */}
      <AnimatePresence>
        {showSuccess && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center px-6 bg-black/80 backdrop-blur-sm"
            onClick={() => setShowSuccess(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              className="bg-gradient-to-br from-amber-600 to-orange-600 w-full max-w-sm rounded-[40px] p-8 text-center space-y-6 shadow-2xl"
            >
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto shadow-lg">
                <CheckCircle2 size={40} className="text-emerald-500" />
              </div>
              <div className="space-y-2">
                <h2 className="text-3xl font-black text-white">Forged! 🔥</h2>
                <p className="text-amber-100 italic">"{purchasedItem}" is now active!</p>
              </div>
              <p className="text-white/80 text-sm">
                Your boost is ready. Make the most of it!
              </p>
              <button
                onClick={() => {
                  setShowSuccess(false);
                  // Offer ad for bonus
                  setTimeout(() => {
                    piNetworkAdsManager.handleRewardedAdFlowFront(user?.id || '');
                  }, 500);
                }}
                className="w-full py-4 bg-white text-amber-700 rounded-2xl font-bold hover:bg-slate-100"
              >
                Continue Forging
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ForgePage;
